<template>
    <div
        class="mb-[42px] mt-[42px] flex flex-wrap items-start justify-between gap-4"
    >
        <div>
            <h1 class="text-[21px] font-medium leading-normal text-gray-800">
                {{ t("vue.dashboard_overview_title") }}
            </h1>
            <p class="mt-1 text-[13px] leading-[18px] text-gray-500">
                {{ t("vue.dashboard_overview_subtitle") }}
            </p>
        </div>

        <div class="flex items-center gap-4">
            <span class="flex items-center gap-2 text-sm text-gray-600">
                <span class="h-2 w-2 rounded-full bg-green-500" />
                {{ dashboard.team.name }}
            </span>
            <Link
                :href="route('home', { view: 'legacy' })"
                class="text-[13px] font-medium leading-[18px] text-[#088BEA] hover:underline"
            >
                {{ t("vue.dashboard_overview_switch_to_legacy") }}
            </Link>
        </div>
    </div>

    <div :class="entranceClass" :style="resolveEntranceDelay(0)">
        <DashboardSummary
            class="mb-[21px]"
            :domains="dashboard.domains"
            :dmarc="dashboard.dmarc"
            :reporting="dashboard.reporting"
            :attention-count="attentionItems.length"
        />
    </div>

    <div
        :class="entranceClass"
        :style="resolveEntranceDelay(1)"
        class="mb-[21px] grid grid-cols-1 gap-[21px] xl:grid-cols-[minmax(0,1.18fr)_minmax(340px,0.82fr)]"
    >
        <NeedsAttention :items="attentionItems" />
        <AccountHealth
            :health="dashboard.health"
            :blacklist="blacklistState"
            :blacklist-loading="isDeferredLoading"
        />
    </div>

    <div
        :class="entranceClass"
        :style="resolveEntranceDelay(2)"
        class="mb-[21px] grid grid-cols-1 gap-[21px] lg:grid-cols-2 xl:grid-cols-3"
    >
        <DomainPortfolio :domains="dashboard.domains" />
        <MonitoringOverview
            :services="dashboard.monitoring"
            :blacklist="blacklistState"
            :blacklist-loading="isDeferredLoading"
        />
        <TeamSubscription
            :team="dashboard.team"
            :subscription="dashboard.subscription"
            :next-billing-at="nextBillingAt"
            :billing-loading="isDeferredLoading"
        />
    </div>

    <div
        :class="entranceClass"
        :style="resolveEntranceDelay(3)"
        class="mb-[42px]"
    >
        <RecentActivity :items="dashboard.activity" />
    </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from "vue";
import { Link } from "@inertiajs/vue3";
import axios from "axios";
import route from "ziggy-js";
import { useTranslations } from "@/composables/useTranslations";
import { useDashboardEntrance } from "./composables/useDashboardEntrance";
import DashboardSummary from "./components/DashboardSummary.vue";
import NeedsAttention from "./components/NeedsAttention.vue";
import AccountHealth from "./components/AccountHealth.vue";
import DomainPortfolio from "./components/DomainPortfolio.vue";
import MonitoringOverview from "./components/MonitoringOverview.vue";
import TeamSubscription from "./components/TeamSubscription.vue";
import RecentActivity from "./components/RecentActivity.vue";
import type {
    IAttentionItem,
    IDashboardDeferredData,
    IDashboardOverview,
    IMonitoringServiceState,
} from "@/types/Dashboard/IDashboardOverview";

const props = defineProps<{
    dashboard: IDashboardOverview;
}>();

const { t } = useTranslations();
const { entranceClass, resolveEntranceDelay } = useDashboardEntrance();

const SEVERITY_PRIORITY: Record<string, number> = {
    critical: 3,
    warning: 2,
    info: 1,
};

const blacklistState = ref<IMonitoringServiceState | null>(null);
const blacklistAttention = ref<IAttentionItem | null>(null);
const nextBillingAt = ref<string | null>(null);
const isDeferredLoading = ref<boolean>(true);

const attentionItems = computed<IAttentionItem[]>(() => {
    const items = [...props.dashboard.attention];

    if (blacklistAttention.value !== null) {
        items.push(blacklistAttention.value);
    }

    return items.sort(
        (first, second) =>
            (SEVERITY_PRIORITY[second.severity] ?? 0) -
            (SEVERITY_PRIORITY[first.severity] ?? 0),
    );
});

const loadDeferredData = async (): Promise<void> => {
    try {
        const response = await axios.get<IDashboardDeferredData>(
            route("dashboard.deferred"),
        );

        blacklistState.value = response.data.blacklist;
        blacklistAttention.value = response.data.blacklistAttention;
        nextBillingAt.value = response.data.nextBillingAt;
    } finally {
        isDeferredLoading.value = false;
    }
};

onMounted(loadDeferredData);
</script>

<style scoped>
.dashboard-entrance {
    animation-duration: 420ms;
    animation-timing-function: cubic-bezier(0.22, 1, 0.36, 1);
    animation-fill-mode: both;
}

.dashboard-entrance--rise {
    animation-name: dashboard-rise;
}

.dashboard-entrance--expand {
    animation-name: dashboard-expand;
}

.dashboard-entrance--drift {
    animation-name: dashboard-drift;
}

.dashboard-entrance--unblur {
    animation-name: dashboard-unblur;
}

@keyframes dashboard-rise {
    from {
        opacity: 0;
        transform: translateY(14px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes dashboard-expand {
    from {
        opacity: 0;
        transform: scale(0.97);
    }
    to {
        opacity: 1;
        transform: scale(1);
    }
}

@keyframes dashboard-drift {
    from {
        opacity: 0;
        transform: translateX(-18px);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
}

@keyframes dashboard-unblur {
    from {
        opacity: 0;
        filter: blur(6px);
    }
    to {
        opacity: 1;
        filter: blur(0);
    }
}

@media (prefers-reduced-motion: reduce) {
    .dashboard-entrance {
        animation: none;
    }
}
</style>
